import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Logger } from 'shared';
import { FeatureFlagsService } from './feature-flags.service';

const MAINTENANCE_PATH = 'maintenance';

const langFromUrl = (url: string): string => {
  const seg = url.split('/').filter(Boolean)[0];
  return seg && /^[a-z]{2}$/i.test(seg) ? seg : (localStorage.getItem('language') || 'en');
};

/**
 * Redirects all navigations to `/:lang/maintenance` while feature flag `maintenanceMode` is true.
 * Apply this on the `:lang` route via `canActivate` / `canActivateChild` so that no app route is reachable.
 */
export const maintenanceGuard: CanActivateFn = (route, state) => {
  const ffs = inject(FeatureFlagsService);
  const router = inject(Router);
  const logger = new Logger('maintenanceGuard');

  if (!ffs.isMaintenanceMode()) {
    return true;
  }

  if (state.url.includes(`/${MAINTENANCE_PATH}`)) {
    return true;
  }

  const lang = langFromUrl(state.url);
  logger.debug(`MAINTENANCE MODE ACTIVE => redirecting ${state.url} to /${lang}/${MAINTENANCE_PATH}`);
  return router.parseUrl(`/${lang}/${MAINTENANCE_PATH}`);
};

/**
 * Counterpart guard for the maintenance route itself: when maintenance mode is OFF,
 * redirect users away from `/:lang/maintenance` to the dashboard so they don't get stuck.
 */
export const noMaintenanceGuard: CanActivateFn = (route, state) => {
  const ffs = inject(FeatureFlagsService);
  const router = inject(Router);

  if (ffs.isMaintenanceMode()) {
    return true;
  }

  const lang = langFromUrl(state.url);
  return router.parseUrl(`/${lang}/dashboard`);
};
